/**
 * Same-day booking cutoff (Son's Island day cabanas).
 *
 * Day cabanas stay sellable for the current day until 6:00 PM Central. After
 * that the day is closed off cleanly so the calendar can't advertise a price
 * for a date the booking step can no longer sell.
 *
 * Evaluated in America/Chicago regardless of the visitor's device timezone,
 * and shared by the calendar and the availability step so the two can never
 * disagree.
 */
export const SAME_DAY_CUTOFF_HOUR_CT = 18;

const CT_PARTS = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Chicago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  hour12: false,
});

function centralParts(now: Date): { date: string; hour: number } {
  const parts = CT_PARTS.formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const hourRaw = get("hour");
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    // Intl can emit "24" for midnight in hour12:false.
    hour: Number(hourRaw) % 24,
  };
}

/** Today's date (yyyy-MM-dd) in Central time. */
export function todayCentral(now: Date = new Date()): string {
  return centralParts(now).date;
}

/** True once the local clock passes the same-day cutoff in Central time. */
export function isPastSameDayCutoff(now: Date = new Date()): boolean {
  return centralParts(now).hour >= SAME_DAY_CUTOFF_HOUR_CT;
}

/**
 * True when `dateISO` (yyyy-MM-dd) is today in Central time AND the cutoff
 * has passed — i.e. the date should no longer be offered or priced.
 */
export function isSameDayClosed(dateISO: string, now: Date = new Date()): boolean {
  const { date, hour } = centralParts(now);
  return dateISO === date && hour >= SAME_DAY_CUTOFF_HOUR_CT;
}
